import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MessageService } from 'primeng/api';
import { BetaFeature } from 'app/models';
import { AppDataService } from 'app/services/app.data.service';
import { XpressPaymentTransactionService } from 'app/services/xpress-payment-transaction.service';

@Component({
    selector: 'anti-fraud-actions',
    templateUrl: './anti-fraud-actions.component.html'
})
export class AntiFraudActionsComponent implements OnInit {
    @Input() transactionId: number = 0;
    @Input() transactionStatus: string = '';
    // Fail-open on absence: legacy DTOs may not surface these flags; the backend re-validates.
    @Input() isPaid?: boolean;
    @Input() isVoided?: boolean;

    @Output() actionCompleted = new EventEmitter<void>();

    hasAccessToAntiFraud: boolean = false;

    showDialogFreezeTransaction: boolean = false;
    showDialogReverseHold: boolean = false;
    explanationForFreeze: string | null = null;
    explanationForReverse: string | null = null;
    freezeErrorMessage: string | null = null;
    reverseErrorMessage: string | null = null;

    isFreezeTransactionButtonDisabled: boolean = false;
    isReleaseHoldButtonDisabled: boolean = false;
    isReverseHoldButtonDisabled: boolean = false;

    constructor(
        private appDataService: AppDataService,
        private xpressPaymentTransactionService: XpressPaymentTransactionService,
        private messageService: MessageService
    ) { }

    ngOnInit(): void {
        this.hasAccessToAntiFraud = this.appDataService.betaFeaturesIsExist([BetaFeature.ANTI_FRAUD_TRANSACTIONS]);
    }

    get canFreeze(): boolean {
        return this.hasAccessToAntiFraud
            && this.isPaid !== false
            && this.isVoided !== true
            && this.transactionStatus !== 'ON_HOLD'
            && this.transactionStatus !== 'HOLD_RELEASED'
            && this.transactionStatus !== 'REVERSED';
    }

    get canReleaseOrReverse(): boolean {
        return this.hasAccessToAntiFraud && this.transactionStatus === 'ON_HOLD';
    }

    openDialogFreezeTransaction(): void {
        this.explanationForFreeze = null;
        this.freezeErrorMessage = null;
        this.showDialogFreezeTransaction = true;
    }

    closeDialogFreezeTransaction(): void {
        this.showDialogFreezeTransaction = false;
        this.freezeErrorMessage = null;
    }

    openDialogReverseHold(): void {
        this.explanationForReverse = null;
        this.reverseErrorMessage = null;
        this.showDialogReverseHold = true;
    }

    closeDialogReverseHold(): void {
        this.showDialogReverseHold = false;
        this.reverseErrorMessage = null;
    }

    freezeTransaction(): void {
        if (!this.explanationForFreeze) {
            this.freezeErrorMessage = 'Explanation is required';
            return;
        }

        this.freezeErrorMessage = null;
        this.isFreezeTransactionButtonDisabled = true;

        this.xpressPaymentTransactionService.freezeTransaction({
            TransactionId: this.transactionId,
            Reason: this.explanationForFreeze
        }, this.messageService).then(
            res => {
                if (res.Success) {
                    this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Transaction frozen successfully' });
                    this.closeDialogFreezeTransaction();
                    this.actionCompleted.emit();
                } else {
                    this.freezeErrorMessage = res.ErrorMessage || 'Unable to freeze the transaction.';
                }
                this.isFreezeTransactionButtonDisabled = false;
            }).catch(err => {
                this.freezeErrorMessage = 'An error occurred while freezing the transaction.';
                this.isFreezeTransactionButtonDisabled = false;
            });
    }

    releaseHold(): void {
        this.isReleaseHoldButtonDisabled = true;

        this.xpressPaymentTransactionService.releaseHold({
            TransactionId: this.transactionId
        }, this.messageService).then(
            res => {
                if (res.Success) {
                    this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Hold released successfully' });
                    this.actionCompleted.emit();
                } else {
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: res.ErrorMessage || 'Unable to release the hold.' });
                }
                this.isReleaseHoldButtonDisabled = false;
            }).catch(err => {
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'An error occurred while releasing the hold' });
                this.isReleaseHoldButtonDisabled = false;
            });
    }

    reverseHold(): void {
        if (!this.explanationForReverse) {
            this.reverseErrorMessage = 'Explanation is required';
            return;
        }

        this.reverseErrorMessage = null;
        this.isReverseHoldButtonDisabled = true;

        this.xpressPaymentTransactionService.reverseHold({
            TransactionId: this.transactionId,
            Reason: this.explanationForReverse
        }, this.messageService).then(
            res => {
                if (res.Success) {
                    this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Transaction reversed successfully' });
                    this.closeDialogReverseHold();
                    this.actionCompleted.emit();
                } else {
                    this.reverseErrorMessage = res.ErrorMessage || 'Unable to reverse the transaction.';
                }
                this.isReverseHoldButtonDisabled = false;
            }).catch(err => {
                this.reverseErrorMessage = 'An error occurred while reversing the transaction.';
                this.isReverseHoldButtonDisabled = false;
            });
    }
}
